export interface CookieConsent {
	analytics: boolean;
	googleMaps: boolean;
}

const STORAGE_KEYS = {
	interacted: "cookie-notice-interacted",
	analytics: "cookies-accepted-analytics",
	googleMaps: "cookies-accepted-google-maps",
} as const;

const consent = reactive<CookieConsent>({
	analytics: false,
	googleMaps: false,
});

const cookiesInteracted = ref(false);

// avoids flashing the notice before the stored preferences have been read
const isReady = ref(false);

// lets an already-interacted visitor reopen the notice (eg. from the footer link)
const forceVisible = ref(false);

function loadCookieValue() {
	cookiesInteracted.value =
		localStorage.getItem(STORAGE_KEYS.interacted) === "1";
	consent.analytics = localStorage.getItem(STORAGE_KEYS.analytics) === "1";
	consent.googleMaps = localStorage.getItem(STORAGE_KEYS.googleMaps) === "1";
}

function persistConsent(next: CookieConsent) {
	localStorage.setItem(STORAGE_KEYS.interacted, "1");
	localStorage.setItem(STORAGE_KEYS.analytics, next.analytics ? "1" : "0");
	localStorage.setItem(STORAGE_KEYS.googleMaps, next.googleMaps ? "1" : "0");

	consent.analytics = next.analytics;
	consent.googleMaps = next.googleMaps;
	cookiesInteracted.value = true;
	forceVisible.value = false;
}

function acceptAll() {
	persistConsent({ analytics: true, googleMaps: true });
}

function rejectAll() {
	persistConsent({ analytics: false, googleMaps: false });
}

function savePreferences(preferences: CookieConsent) {
	persistConsent(preferences);
}

// used by features (eg. the map) that offer their own inline opt-in
// instead of sending the user back to the cookie notice
function enableConsent(key: keyof CookieConsent) {
	persistConsent({ ...consent, [key]: true });
}

function reopenNotice() {
	forceVisible.value = true;
}

const cookieNoticeVisible = computed(
	() => isReady.value && (forceVisible.value || !cookiesInteracted.value),
);

export function useCookies() {
	onBeforeMount(() => {
		loadCookieValue();
	});

	onMounted(() => {
		setTimeout(() => {
			isReady.value = true;
		}, 1000);
	});

	return {
		consent,
		cookiesInteracted,
		cookieNoticeVisible,
		acceptAll,
		rejectAll,
		savePreferences,
		enableConsent,
		reopenNotice,
	};
}
